import {
  HStack,
  Button,
  Tooltip,
  IconButton,
  Box,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FaHourglassHalf } from "react-icons/fa";
import { FiAlertTriangle } from "react-icons/fi";
import { GiCardExchange } from "react-icons/gi";
import { GrUpload } from "react-icons/gr";
import { RiMenuAddFill } from "react-icons/ri";
import GaraazAlertDialogue from "../../../components/GaraazAlertDialogue";
import TextInputModal from "../../../components/TextInputModal";
import {
  OrderStatus,
  OrderActions,
  OrdersTextLabels,
} from "../enums";
import GaraazChoser from "./GaraazChoser";
import GaraazDatePickerModalETA from "./GaraazDatePickerModalETA";
import GaraazGeneralPopover from "./GaraazGeneralPopover";

import UploadInvoiceModal, { invoiceInfoType } from "./UploadInvoiceModal";
import { useOrdersStore } from "../store/useOrdersStore";
import { useGetOrderMediaConfig } from "../../PlaceOrderTest/queryHooks";
import { startOrcaStates } from "./GaraazOrderComponent";
import { PersonOutlineOutlined, ThumbDownAlt } from "@mui/icons-material";
import ThumbUpAltIcon from "@mui/icons-material/ThumbUpAlt";
import services from "../services";
import useNotification, {
  useGaraazToastWithChildren,
} from "../../../utils/useNotification";
import OrderStatusChangeErrorBox from "./OrderStatusChangeErrorBox";
import { useUserDetailsContext } from "../../../context/userDetailsContext";
import GaraazCountDownClockV2 from "./GaraazCountDownClockV2";
import InvoicePopver from "./InvoicePopover";
import { dateFormatInvoiceApi } from "../../../utils/dateResolvers";
import Brightness1Icon from "@mui/icons-material/Brightness1";
import InvoicePopoverTrigger from "../../../components/InvoicePopoverTrigger";
import updateOrderHandlerAllocationTime from '../../../utils/updateOrderHandlerAllocationTime';
import updateAckTime from '../../../utils/updateAckTime';
import { OrderRelationType } from "../store/orderStrategies";
import { changeParticipantInConversation } from "../../../utils/conversationUtils";

type GaraazOrderComponentFooterSubscriptionProps = {
  orderData: any;
  orderStatus: any;
  orderCancellationReason: string[];
  setOrderStatus: React.Dispatch<any>;
  handlePartConversionChangeScreen: () => void;
  refetchAllOrders: () => void;
  setEtaMissed: React.Dispatch<React.SetStateAction<boolean>>;
  isCustomerOrderFreezed: boolean;
  isChildOrder: boolean;
  isChildOrdersVisible: boolean;
};

const GaraazOrderComponentFooterSubscription: React.FC<
  GaraazOrderComponentFooterSubscriptionProps
> = ({
  orderData,
  orderStatus,
  handlePartConversionChangeScreen,
  refetchAllOrders,
  setOrderStatus,
  setEtaMissed,
  isCustomerOrderFreezed,
  isChildOrder,
  isChildOrdersVisible,
}) => {  
  const toast = useNotification();
    const garaazToast = useGaraazToastWithChildren();
    const userDetails = useUserDetailsContext();
    const orderEta = useOrdersStore((state) => state.orderEtas[orderData._id]);
    const setSelectedOrderHandler = useOrdersStore(
      useCallback((store) => store.setSelectedOrderHandler, [])
    );
    const setOrderEtasWithOrderId = useOrdersStore(
      useCallback((state) => state.setOrderEtasWithOrderId, [])
    );
    const savedOrderRemarks = useOrdersStore(
      useCallback((state) => state.orderRemarks[orderData._id], [orderData._id])
    );
    const setSavedOrderRemarks = useOrdersStore(
      useCallback((state) => state.setOrderRemarksWithOrderId, [])
    );
  const setOrderMetaInfoWithOrderId = useOrdersStore(
    useCallback((state) => state.setOrderMetaInfoWithOrderId, [])
  );

  const { data: mobileConfigs } = useGetOrderMediaConfig();

  const orderHandlers = useOrdersStore((state) => state.orderHandlers);
  const [invoiceInfo, setInvoiceInfo] = useState({
    title: "Garaaz",
    invoiceMediaId: orderData?.invoiceInfo?.invoiceMediaId ?? "",
    mrpInvoiceMediaId: orderData?.invoiceInfo?.mrpInvoiceMediaId ?? "",
    invoiceNo: orderData?.invoiceInfo?.invoiceNo ?? "",
    invoiceAmount: orderData?.invoiceInfo?.invoiceAmount ?? "",
    invoiceDate: orderData?.invoiceInfo?.invoiceDate ?? "",
    invoiceStatus: orderData?.invoiceInfo?.invoiceStatus ?? "",
    paymentLink: orderData?.invoiceInfo?.paymentLink ?? "",
  });

  const [challanInfo, setChallanInfo] = useState({
    title: "Garaaz",
    challanMediaId: orderData?.challanInfo?.challanMediaId ?? "",
    challanNo: orderData?.challanInfo?.challanNo ?? "",
    challanAmount: orderData?.challanInfo?.challanAmount ?? "",
    challanDate: orderData?.challanInfo?.challanDate ?? "",
  });

  const [creditNoteInfo, setCreditNoteInfo] = useState({
    title: "Garaaz",
    creditNoteMediaId: orderData?.creditNoteInfo?.creditNoteMediaId ?? "",
    creditNoteNo: orderData?.creditNoteInfo?.creditNoteNo ?? "",
    creditNoteAmount: orderData?.creditNoteInfo?.creditNoteAmount ?? "",
    creditNoteDate: orderData?.creditNoteInfo?.creditNoteDate ?? "",
  });

  const [ackClockValue0, setAckClockValue0] = useState(
    Math.floor(new Date(orderData.orderDate).getTime() / 1000)
  );

  const [ackClockValue100, setAckClockValue100] = useState(
    Math.floor(new Date(orderData.orderDate).getTime() / 1000) +
      mobileConfigs?.threshold_ack_order_mins * 60
  );

  const [customerSupplierAssociations, setCustomerSupplierAssociations] =
    useState<{}[]>([]);
  const [cancellationReason, setCancellationReason] = useState(
    orderData?.cancellationReason ?? ""
  );
  const [ackTimeMissed, setAckTimeMissed] = useState(false);

  const orderHandlerOptions = orderHandlers.map((orderHandler) => {
    return {
      name: orderHandler.user.firstName + " " + orderHandler.user.lastName,
      menuItemIcon: (
        <Brightness1Icon
          sx={{
            color: orderHandler?.isActive ? "green" : "red",
            fontSize: "10px",
          }}
        />
      ),
      ...orderHandler,
    };
  });
  const orderHandlerName =
    (orderData?.orderHandler[0]?.firstName ?? "-") +
    " " +
    (orderData?.orderHandler[0]?.lastName ?? "");

  const {
    isOpen: etaPickerModalOpen,
    onOpen: openETAPickerModal,
    onClose: closeETAPickerModal,
  } = useDisclosure();

  const {
    isOpen: updateInvoiceModalOpen,
    onOpen: openUpdateInvoiceModal,
    onClose: closeUpdateInvoiceModal,
  } = useDisclosure();

  const {
    isOpen: orderRemarksModelOpen,
    onOpen: openOrdersRemarksModel,
    onClose: closeOrdersRemarksModel,
  } = useDisclosure();

  const {
    isOpen: isOpenACCEPT_RETURNOrderDialogue,
    onOpen: onOpenACCEPT_RETURNOrderDialogue,
    onClose: onCloseACCEPT_RETURNOrderDialogue,
  } = useDisclosure();

  const {
    isOpen: isOpenREJECT_RETURNOrderDialogue,
    onOpen: onOpenREJECT_RETURNOrderDialogue,
    onClose: onCloseREJECT_RETURNOrderDialogue,
  } = useDisclosure();
  useEffect(() => {
    let customerSupplierAssociationsTemp: {}[] = [];

    orderData?.customer?.supplierAssociations?.map(
      (association: any, index: number) => {
        let customerSupplierAssociationObject = {
          "Supplier Name": association?.supplier?.name
            ? association?.supplier?.businessName
            : "-",
          "Branch Name": association?.supplier?.branches
            ? association?.supplier?.branches[0]?.branchName
            : "-",

          "Branch Code": association?.supplier?.branches
            ? association?.supplier?.branches[0]?.branchCode
            : "-",
          "Supplier Code": association?.supplierCode
            ? association.supplierCode
            : "-",
        };
        customerSupplierAssociationsTemp.push(
          customerSupplierAssociationObject
        );
      }
    );
    setCustomerSupplierAssociations(customerSupplierAssociationsTemp);
    setInvoiceInfo({
      title: "Garaaz",
      invoiceMediaId: orderData?.invoiceInfo?.invoiceMediaId,
      mrpInvoiceMediaId: orderData?.invoiceInfo?.mrpInvoiceMediaId,
      invoiceNo: orderData?.invoiceInfo?.invoiceNo,
      invoiceDate: orderData?.invoiceInfo?.invoiceDate,
      invoiceAmount: orderData?.invoiceInfo?.invoiceAmount,
      invoiceStatus: orderData?.invoiceInfo?.invoiceStatus,
      paymentLink: orderData?.invoiceInfo?.paymentLink,
    });
    setChallanInfo({
      title: "Garaaz",
      challanMediaId: orderData?.challanInfo?.challanMediaId,
      challanNo: orderData?.challanInfo?.challanNo,
      challanDate: orderData?.challanInfo?.challanDate,
      challanAmount: orderData?.challanInfo?.challanAmount,
    });

    setCreditNoteInfo({
      title: "Garaaz",
      creditNoteMediaId: orderData?.creditNoteInfo?.creditNoteMediaId,
      creditNoteNo: orderData?.creditNoteInfo?.creditNoteNo,
      creditNoteAmount: orderData?.creditNoteInfo?.creditNoteAmount,
      creditNoteDate: orderData?.creditNoteInfo?.creditNoteDate,
    });

    setAckClockValue0(
      Math.floor(new Date(orderData.orderDate).getTime() / 1000)
    );
    setAckClockValue100(
      Math.floor(new Date(orderData.orderDate).getTime() / 1000) +
        mobileConfigs?.threshold_ack_order_mins * 60
    );
    setAckTimeMissed(false);
  }, [orderData]);

  const saveOrderRemarks = async (remarks: string) => {
    try {
      let newOrderRemarks = [
        ...savedOrderRemarks,
        {
          userName: `${userDetails.firstName} ${userDetails.lastName}`,
          remarks: remarks,
        },
      ];
      setSavedOrderRemarks(orderData._id, newOrderRemarks);
      await services.setOrderRemarks(orderData._id, {
        userName: `${userDetails.firstName} ${userDetails.lastName}`,
        remarks,
      });
    } catch (error) {}
  };

  const handleorderETAModalSubmit = async (
    etaGivenTime: string,
    eta: string,
    reason: string
  ) => {
    try {
      const newEtaChangeReason = [...orderEta, { etaGivenTime, eta, reason }];
      setOrderEtasWithOrderId(orderData._id, newEtaChangeReason);
      await services.setOrderETA(orderData._id, {
        etaGivenTime,
        eta,
        reason,
      });
      setEtaMissed(false);
    } catch (error) {}
  };

  const handleOrderHandlerChange = async (item: any) => {
    try {
      await services.updateOrder(orderData._id, {
        orderHandler: item.userId,
      });
      await updateOrderHandlerAllocationTime(orderData._id, setOrderMetaInfoWithOrderId);
      
      try {
        const currentOrderHandler = orderData.orderHandler?.[0];
        const activeSupplier = orderData.allocatedTo?.find(
          (item: any) => item.isActive === true
        );

        if (
          !activeSupplier?.supplierId ||
          !orderData?.orderNo ||
          !currentOrderHandler?._id
        ) {
          console.warn("Participant change skipped: missing prerequisites", {
            hasActiveSupplier: !!activeSupplier?.supplierId,
            hasOrderNo: !!orderData?.orderNo,
            hasCurrentHandler: !!currentOrderHandler?._id,
          });
        } else {
          const uniqueNameForSupplierConv = `order:${orderData.orderNo}|supplier:${activeSupplier.supplierId}`;
          const uniqueNameForCustomerConv = `order:${orderData.orderNo}|customer:${orderData.customerId}`;
          await changeParticipantInConversation({
            conversationName: uniqueNameForSupplierConv,
            oldHandlerUserId: currentOrderHandler._id,
            newHandlerUserId: item.userId,
          });

          await changeParticipantInConversation({
            conversationName: uniqueNameForCustomerConv,
            oldHandlerUserId: currentOrderHandler._id,
            newHandlerUserId: item.userId,
          });
        }
      } catch (error) {
        console.warn("Participant change failed:", error);
      }
      setSelectedOrderHandler(orderData._id, item.user, OrderRelationType.ROOT);
    } catch (error) {
      console.error('Failed to assign order handler:', error);
    }
  };

  const handleChangeOrderStatus = async (
    status: OrderStatus | OrderActions,
    modalCloseFunction?: () => void
  ) => {
    try {
      if (status === OrderStatus.ACK) {
          await updateAckTime(orderData._id, setOrderMetaInfoWithOrderId);
      }
      await services.checkNextStateOfGaraazOrders(orderData._id, status);
      setOrderStatus(status);
      !!refetchAllOrders && refetchAllOrders();
    } catch (error: any) {
      const errorFromResponse = error?.response?.data?.errorMsg;
      const title = !!errorFromResponse.length
        ? errorFromResponse[0]?.title
        : OrdersTextLabels.ACCESS_DENIED_ORDER_STATUS_INVALID;
      garaazToast({
        title: title,
        status: "error",
        render: <OrderStatusChangeErrorBox errorMsg={errorFromResponse} />,
      });
    } finally {
      if (modalCloseFunction) modalCloseFunction();
    }
  };

  const handleUpdateInvoiceModalSubmit = async (
    pdfFile: File | null = null,
    invoiceInfo: invoiceInfoType,
    challanInfo: any,
    fileType: string
  ) => {
    try {
      let updatedParts = {};
      if (fileType === "Invoice") {
        if (
          pdfFile !== null &&
          invoiceInfo.invoiceNo &&
          invoiceInfo.invoiceAmount
        ) {
          const formData = new FormData();
          formData.append("media", pdfFile);
          const mediaResult = await services.addMedia(formData);

          updatedParts = {
            invoiceInfo: {
              ...invoiceInfo,
              invoiceMediaId: mediaResult[0]._id,
              invoiceDate: dateFormatInvoiceApi(invoiceInfo.invoiceDate),
            },
          };
        } else {
          updatedParts = {
            invoiceInfo: {
              ...invoiceInfo,
              invoiceDate: dateFormatInvoiceApi(invoiceInfo.invoiceDate),
              invoiceMediaId:
                invoiceInfo.invoiceNo &&
                invoiceInfo.invoiceAmount &&
                invoiceInfo.invoiceDate
                  ? orderData?.invoiceInfo?.invoiceMediaId
                  : undefined,
            },
          };
        }

        const updatedOrderResponse = await services.updateOrder(
          orderData._id,
          updatedParts
        );

        setInvoiceInfo({
          title: "Garaaz",
          invoiceMediaId: updatedOrderResponse.invoiceInfo.invoiceMediaId,
          mrpInvoiceMediaId: updatedOrderResponse.invoiceInfo.mrpInvoiceMediaId,
          invoiceNo: updatedOrderResponse.invoiceInfo.invoiceNo,
          invoiceAmount: updatedOrderResponse.invoiceInfo.invoiceAmount,
          invoiceDate: updatedOrderResponse.invoiceInfo.invoiceDate,
          invoiceStatus: updatedOrderResponse.invoiceInfo.invoiceStatus,
          paymentLink: updatedOrderResponse.invoiceInfo.paymentLink,
        });

        toast({
          description: OrdersTextLabels.INVOICE_UPLOADED_SUCCESSFULLY,
          status: "success",
        });
      } else {
        if (
          pdfFile !== null &&
          challanInfo.challanNo &&
          challanInfo.challanAmount
        ) {
          const formData = new FormData();
          formData.append("media", pdfFile);
          const mediaResult = await services.addMedia(formData);

          updatedParts = {
            challanInfo: {
              ...challanInfo,
              challanMediaId: mediaResult[0]._id,
              challanDate: dateFormatInvoiceApi(challanInfo.challanDate),
            },
          };
        } else {
          updatedParts = {
            challanInfo: {
              ...challanInfo,
              challanDate: dateFormatInvoiceApi(challanInfo.challanDate),
              challanMediaId:
                challanInfo.challanNo &&
                challanInfo.challanAmount &&
                challanInfo.challanDate
                  ? orderData?.challanInfo?.challanMediaId
                  : undefined,
            },
          };
        }

        const updatedOrderResponse = await services.updateOrder(
          orderData._id,
          updatedParts
        );

        setChallanInfo({
          title: "Garaaz",
          challanMediaId: updatedOrderResponse.challanInfo.challanMediaId,
          challanNo: updatedOrderResponse.challanInfo.challanNo,
          challanAmount: updatedOrderResponse.challanInfo.challanAmount,
          challanDate: updatedOrderResponse.challanInfo.challanDate,
        });

        toast({
          description: OrdersTextLabels.CHALLAN_UPLOADED_SUCCESSFULLY,
          status: "success",
        });
      }
    } catch (error) {
      toast({
        description: OrdersTextLabels.SOMETHING_WENT_WRONG,
        status: "error",
      });
    }
  };

  const uniqueNumbersInQuickEtas = useMemo(() => {
    return mobileConfigs?.quick_etas_mins
      ?.split(",")
      ?.filter(
        (value: any, index: number, array: any) =>
          array.indexOf(value) === index && !isNaN(parseInt(value))
      );
  }, [mobileConfigs]);

  async function onAckTimeComplete() {
    const response = await services.throwACKMissedEvent(orderData._id);
  }

    return (
      <HStack
        flex={1}
        height={"12%"}
        borderRadius={"0px 0px 8px 8px"}
        borderTop={"1px solid lightgrey"}
        borderBottom={"1px solid lightgrey"}
        style={{
          margin: 0,
        }}
        display={"flex"}
        padding={`${startOrcaStates.includes(orderStatus) ? 0 : "0.8rem"}`}
        py={`${startOrcaStates.includes(orderStatus) ? 0 : "1.5rem"}`}
        alignItems={"center"}
        bgColor={"white"}
      >
        {startOrcaStates.includes(orderStatus) ? (
          <>
            <Button
              borderTopLeftRadius={0}
              borderTopRightRadius={0}
              height={"100%"}
              width={"100%"}
              bg={"green.500"}
              _hover={{ bg: "green.600" }}
              color={"white"}
              onClick={() => handleChangeOrderStatus(OrderStatus.ACK)}
            >
              <Text fontSize={"lg"}>ACK</Text>
              {!orderEta.length && (
                <Box ml="5" display={"flex"}>
                  {!ackTimeMissed && (
                    <GaraazCountDownClockV2
                      componentSize="small"
                      value0={ackClockValue0}
                      value100={ackClockValue100}
                      onCompleteTime={onAckTimeComplete}
                      isCompleted={ackTimeMissed}
                      setIsCompleted={setAckTimeMissed}
                    ></GaraazCountDownClockV2>
                  )}

                  {ackTimeMissed && (
                    <HStack spacing={1}>
                      <FiAlertTriangle
                        color="red"
                        style={{ marginLeft: "2px" }}
                      />
                      <Text>ACK Missed</Text>
                    </HStack>
                  )}
                </Box>
              )}
            </Button>
          </>
        ) : (
          <>
            <HStack spacing={3} w="45%">
              <Box>
                <Tooltip label="Flip View">
                  <IconButton
                    icon={<GiCardExchange />}
                    backgroundColor={"#BBE6E3"}
                    color={"black"}
                    aria-label={""}
                    onClick={handlePartConversionChangeScreen}
                  />
                </Tooltip>
              </Box>
              <Box>
                <GaraazChoser
                  ButtonIcon={<PersonOutlineOutlined />}
                  ButtonToolTipLabel="Assign Order Handler"
                  MenuItemOnChange={handleOrderHandlerChange}
                  displayKey="name"
                  options={orderHandlerOptions}
                  MenuButtonProps={{ isDisabled: isCustomerOrderFreezed }}
                  showIcon={true}
                />
              </Box>
              <Box>
                <Tooltip label="Customer Accepted">
                  <IconButton
                    icon={<ThumbUpAltIcon />}
                    backgroundColor={"#BBE6E3"}
                    color={"black"}
                    aria-label={""}
                    onClick={onOpenACCEPT_RETURNOrderDialogue}
                    _hover={{ backgroundColor: "green.400" }}
                    isDisabled={isCustomerOrderFreezed}
                  />
                </Tooltip>
                <GaraazAlertDialogue
                  alertBodyText="Are you sure? You can't undo this action afterwards."
                  alertDialogueHeaderText="Customer accepted subscription"
                  onSubmitBtn={() =>
                    handleChangeOrderStatus(
                      OrderActions.ACCEPT,
                      onCloseACCEPT_RETURNOrderDialogue
                    )
                  }
                  isOpen={isOpenACCEPT_RETURNOrderDialogue}
                  onClose={onCloseACCEPT_RETURNOrderDialogue}
                  onOpen={onOpenACCEPT_RETURNOrderDialogue}
                  submitBtnText="Confirm"
                />
              </Box>
              <Box>
                <Tooltip label="Customer Rejected">
                  <IconButton
                    icon={<ThumbDownAlt />}
                    backgroundColor={"#BBE6E3"}
                    color={"black"}
                    aria-label={""}
                    onClick={onOpenREJECT_RETURNOrderDialogue}
                    _hover={{ backgroundColor: "red.400" }}
                    isDisabled={isCustomerOrderFreezed}
                  />
                </Tooltip>
                <GaraazAlertDialogue
                  alertBodyText="Are you sure? You can't undo this action afterwards."
                  alertDialogueHeaderText="Customer rejected subscription"
                  onSubmitBtn={() =>
                    handleChangeOrderStatus(
                      OrderActions.REJECT,
                      onCloseREJECT_RETURNOrderDialogue
                    )
                  }
                  isOpen={isOpenREJECT_RETURNOrderDialogue}
                  onClose={onCloseREJECT_RETURNOrderDialogue}
                  onOpen={onOpenREJECT_RETURNOrderDialogue}
                  submitBtnText="Confirm"
                />
              </Box>

            <Box>
              <Tooltip label="ETA">
                <IconButton
                  icon={<FaHourglassHalf />}
                  backgroundColor={"#BBE6E3"}
                  color={"black"}
                  aria-label={""}
                  onClick={openETAPickerModal}
                  isDisabled={isCustomerOrderFreezed}
                />
              </Tooltip>
              <GaraazDatePickerModalETA
                modelHeader="Please provide ETA for Customer"
                isOpen={etaPickerModalOpen}
                orderETAs={orderEta}
                handleSubmit={handleorderETAModalSubmit}
                onClose={closeETAPickerModal}
                modifiedOrderETAs={uniqueNumbersInQuickEtas}
              />
            </Box>
            <Box>
              <Tooltip label="Upload Invoice">
                <IconButton
                  icon={<GrUpload />}
                  backgroundColor={"#BBE6E3"}
                  color={"black"}
                  aria-label={""}
                  onClick={openUpdateInvoiceModal}
                  isDisabled={isCustomerOrderFreezed}
                />
              </Tooltip>
              <UploadInvoiceModal
                isOpen={updateInvoiceModalOpen}
                orderInvoiceInfo={invoiceInfo}
                handleSubmit={handleUpdateInvoiceModalSubmit}
                onClose={closeUpdateInvoiceModal}
                orderChallanInfo={challanInfo}
              />
            </Box>
            <Box>
              <Tooltip label="Add Order Remarks">
                <IconButton
                  icon={<RiMenuAddFill />}
                  backgroundColor={"#BBE6E3"}
                  color={"black"}
                  aria-label={""}
                  onClick={openOrdersRemarksModel}
                />
              </Tooltip>
              <TextInputModal
                modalHeader="Order Remarks"
                inputPlaceHolder="Add Order Remarks"
                isOpen={orderRemarksModelOpen}
                onSave={saveOrderRemarks}
                onClose={closeOrdersRemarksModel}
              />
            </Box>
          </HStack>
          <Box display={"flex"} style={{ marginLeft: "auto" }} paddingTop={5}>
            <GaraazGeneralPopover
              ArekeysVisible={true}
              CopierKeys={["Supplier Code"]}
              title="Associations"
              value={customerSupplierAssociations}
              placement={"top-start"}
            >
              <InvoicePopoverTrigger
                invoiceStatus={invoiceInfo.invoiceStatus}
              ></InvoicePopoverTrigger>
            </GaraazGeneralPopover>
            {(invoiceInfo?.invoiceNo || creditNoteInfo?.creditNoteNo) && (
              <InvoicePopver
                copierKeys={["invoiceNo", "challanNo", "creditNoteNo"]}
                Invoices={[invoiceInfo]}
                Challans={[challanInfo]}
                CreditNotes={[creditNoteInfo]}
              >
                <InvoicePopoverTrigger
                  invoiceStatus={invoiceInfo.invoiceStatus}
                ></InvoicePopoverTrigger>
              </InvoicePopver>
            )}
            <Box ml={5}>
              <Text fontWeight={"bold"} color={"#1A202C"}>
                HANDLER
              </Text>
              <Text
                color="teal.600"
                textTransform={"uppercase"}
                fontWeight={"semibold"}
                fontSize={"x-small"}
              >
                {`${orderHandlerName}`}
              </Text>
            </Box>
          </Box>
        </>
      )}
    </HStack>
  );
};

export default GaraazOrderComponentFooterSubscription;
